import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Pagination,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { listVideoJobs } from "../api/listVideoJobs";
import { jobModelLabel, truncatePrompt } from "../utils/jobLabels";
import { useVideoProject } from "./VideoProjectContext";
import { jobToProjectClip } from "./jobToClip";
import { MAX_PROJECT_CLIPS } from "./videoProjectConstants";

const PAGE_SIZE = 12;

function SelectableVideoTile({ job, selected, disabled, onToggle, lang }) {
  const hasVideo = job.status === "completed" && Boolean(job.video_url);

  return (
    <Box
      onClick={() => hasVideo && !disabled && onToggle(job.id)}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        opacity: hasVideo ? 1 : 0.45,
        cursor: hasVideo && !disabled ? "pointer" : "not-allowed",
        bgcolor: "#000",
      }}
    >
      <Checkbox
        checked={selected}
        disabled={!hasVideo || disabled}
        onChange={() => onToggle(job.id)}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          top: 4,
          left: 4,
          zIndex: 2,
          bgcolor: alpha("#fff", 0.9),
          borderRadius: 1,
          p: 0.25,
        }}
      />
      <Box sx={{ pt: "56.25%", position: "relative" }}>
        {hasVideo ? (
          <Box
            component="video"
            src={job.video_url}
            muted
            playsInline
            preload="metadata"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              p: 1,
              textAlign: "center",
            }}
          >
            {lang?.videoStitchNotReady || "Not ready"}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 1, bgcolor: "background.paper" }}>
        <Typography variant="caption" fontWeight={800} display="block" noWrap>
          {jobModelLabel(job.ui_model)} · {job.duration_sec || "?"}s
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10 }}>
          {truncatePrompt(job.prompt, 48) || `#${job.id}`}
        </Typography>
      </Box>
    </Box>
  );
}

export default function SelectVideosModal({ open, onClose, lang, extraJob }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { clips, addClips, maxClips, isInProject } = useVideoProject();

  const [loading, setLoading] = React.useState(false);
  const [jobs, setJobs] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());

  const slotsLeft = Math.max(0, maxClips - clips.length);

  const displayJobs = React.useMemo(() => {
    const list = [...jobs];
    if (extraJob?.video_url && extraJob?.id && !list.some((j) => j.id === extraJob.id)) {
      list.unshift(extraJob);
    }
    return list;
  }, [jobs, extraJob]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { jobs: rows, pagination } = await listVideoJobs(hitAxios, {
        page,
        limit: PAGE_SIZE,
        status: "completed",
      });
      setJobs(rows.filter((j) => j.status === "completed" && j.video_url));
      setTotalPages(pagination?.total_pages || 1);
    } catch (err) {
      showSnack?.(err?.message || "Could not load videos.", false);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [hitAxios, page, showSnack]);

  React.useEffect(() => {
    if (!open) return;
    setPage(1);
    setSelectedIds(new Set());
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  React.useEffect(() => {
    if (!open || !extraJob?.video_url) return;
    if (!isInProject(extraJob.video_url)) {
      setSelectedIds((prev) => new Set(prev).add(extraJob.id));
    }
  }, [open, extraJob, isInProject]);

  const toggle = (jobId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else {
        if (next.size >= slotsLeft) return prev;
        next.add(jobId);
      }
      return next;
    });
  };

  const handleAdd = () => {
    const toAdd = [];
    for (const job of displayJobs) {
      if (!selectedIds.has(job.id)) continue;
      if (isInProject(job.video_url)) continue;
      const clip = jobToProjectClip(job);
      if (clip) toAdd.push(clip);
    }

    const { added } = addClips(toAdd);
    if (added > 0) {
      showSnack?.(
        (lang?.videoStitchAddedCount || "Added {n} clip(s) to Clip board.").replace("{n}", String(added)),
        true,
      );
      onClose();
    } else {
      showSnack?.(lang?.videoStitchNothingAdded || "No new clips added.", false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
        <VideoLibraryOutlined color="primary" />
        {lang?.videoStitchSelectTitle || "Select videos for Clip board"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {lang?.videoStitchSelectHint ||
              "Choose completed videos from your library. Select multiple, then add them to Clip board."}
          </Typography>
          {slotsLeft === 0 ? (
            <Alert severity="warning">
              {lang?.videoStitchQueueFull || `Clip board is full (max ${maxClips} clips). Remove a clip to add more.`}
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {(lang?.videoStitchSlotsLeft || "{n} slot(s) remaining").replace("{n}", String(slotsLeft))}
            </Typography>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : displayJobs.length === 0 ? (
            <Alert severity="info">
              {lang?.videoStitchNoVideos ||
                "No completed videos yet. Generate clips in Video Studio first, then return here."}
            </Alert>
          ) : (
            <Grid container spacing={1.5}>
              {displayJobs.map((job) => (
                <Grid item xs={6} sm={4} md={3} key={job.id}>
                  <SelectableVideoTile
                    job={job}
                    selected={selectedIds.has(job.id)}
                    disabled={slotsLeft === 0 && !selectedIds.has(job.id)}
                    onToggle={toggle}
                    lang={lang}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {totalPages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="small"
              />
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          disabled={selectedIds.size === 0 || slotsLeft === 0}
          onClick={handleAdd}
          sx={{ fontWeight: 800 }}
        >
          {(lang?.videoStitchAddSelected || "Add selected") + ` (${selectedIds.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
